'use client';

/**
 * Global error boundary for uncaught errors
 * Catches errors that bubble up from any component in the app
 */

import { useEffect } from 'react';
import { Button } from '@/shared/components/ui/button';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';

export default function ErrorPage({
  error,
  reset,
}: {
  readonly error: Error & { digest?: string };
  readonly reset: () => void;
}) {
  useEffect(() => {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Uncaught Error:', {
        message: error.message,
        stack: error.stack,
        digest: error.digest,
        timestamp: new Date().toISOString()
      });
    }
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="text-center max-w-md">
        {/* Error Icon */}
        <div className="mb-6">
          <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
            <AlertTriangle className="w-10 h-10 text-destructive" />
          </div>
        </div>
        
        {/* Error Message */}
        <h1 className="text-3xl font-bold mb-3 text-foreground">
          Something went wrong!
        </h1>
        <p className="text-muted-foreground mb-2">
          An unexpected error occurred. Don't worry, we've been notified and will look into it.
        </p>
        
        {/* Technical details (dev only) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-4 p-4 bg-muted rounded-lg text-left">
            <p className="text-xs font-mono text-destructive break-all">
              {error.message}
            </p>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex items-center justify-center gap-3 mt-8">
          <Button 
            onClick={reset}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </Button>
          <Button 
            variant="outline" 
            onClick={() => window.location.href = '/dashboard'}
            className="gap-2"
          >
            <Home className="w-4 h-4" />
            Go to Dashboard
          </Button>
        </div>
        
        {/* Help Text */}
        <p className="text-xs text-muted-foreground mt-6">
          If this problem persists, please contact support
        </p>
      </div>
    </div>
  );
}

